import { useState, useEffect, useCallback } from "react";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import {
	useGetZohoListSyncTriggerQuery,
	useUpdateZohoListSyncTriggerMutation,
	useGetZohoOAuthStatusQuery,
} from "@/pages/Private/redux/zoho/zoho.api";

import { FeaturesCodes } from "../../../../Users/enum/codes";
import { ZohoSyncTriggerWithInherit } from "../../../../Company/schema/zoho";

interface UseZohoSyncTriggerOptions {
	listId: number;
}

export const useZohoSyncTrigger = ({ listId }: UseZohoSyncTriggerOptions) => {
	const isZohoEnabled = useIsFeatureActive(FeaturesCodes.ZOHO_CRM);
	const { data: oauthStatus } = useGetZohoOAuthStatusQuery(undefined, {
		skip: !isZohoEnabled,
	});
	const isConnected = oauthStatus?.connected ?? false;

	// API hooks
	const { data: listSyncTrigger } = useGetZohoListSyncTriggerQuery(listId, {
		skip: !isZohoEnabled || !isConnected || !listId,
	});
	const [updateListSyncTrigger] = useUpdateZohoListSyncTriggerMutation();

	// Local state for selected trigger
	const [selectedZohoSyncTrigger, setSelectedZohoSyncTrigger] = useState<
		ZohoSyncTriggerWithInherit | undefined
	>(undefined);

	// Initialize from API data
	useEffect(() => {
		if (listSyncTrigger) {
			setSelectedZohoSyncTrigger(listSyncTrigger.trigger);
		}
	}, [listSyncTrigger]);

	// Check if sync trigger has changed
	const hasSyncTriggerChanged = useCallback(() => {
		return (
			isZohoEnabled &&
			isConnected &&
			selectedZohoSyncTrigger !== undefined &&
			selectedZohoSyncTrigger !== listSyncTrigger?.trigger
		);
	}, [isZohoEnabled, isConnected, selectedZohoSyncTrigger, listSyncTrigger?.trigger]);

	// Save sync trigger to API
	const saveSyncTrigger = useCallback(async () => {
		if (hasSyncTriggerChanged() && selectedZohoSyncTrigger) {
			await updateListSyncTrigger({
				listId,
				data: { trigger: selectedZohoSyncTrigger },
			}).unwrap();
		}
	}, [hasSyncTriggerChanged, selectedZohoSyncTrigger, listId, updateListSyncTrigger]);

	return {
		// Feature flags
		isZohoEnabled,
		isConnected,

		// Sync trigger state
		listSyncTrigger,
		selectedZohoSyncTrigger,
		setSelectedZohoSyncTrigger,

		// Actions
		hasSyncTriggerChanged,
		saveSyncTrigger,
	};
};
